import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Droplets, Loader2, Eye, EyeOff, KeyRound, ArrowLeft } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { listPinUsers, pinLogin, pinRecoveryQuestion, pinResetWithRecovery } from "../lib/api_extra";
import { authMe } from "../lib/api";
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from "../lib/testMode";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
  return String(detail);
}

// Step states: "loading" | "choice" | "password" | "pin-select" | "pin-keypad" | "pin-recovery"
export default function Login() {
  const { login: ctxLogin } = useAuth();
  const loc = useLocation();
  const [step, setStep] = useState("loading");
  const [pinUsers, setPinUsers] = useState([]);
  const [pinSelectedUser, setPinSelectedUser] = useState(null);

  useEffect(() => {
    // UX miglioria: mostra direttamente il form email/password come default per
    // ADMIN/UFFICIO (la maggioranza degli utenti). Il PIN per i trasportatori
    // resta accessibile via link secondario sotto al form.
    listPinUsers()
      .then((u) => {
        setPinUsers(u);
        setStep("password");
      })
      .catch(() => setStep("password"));
  }, []);

  const dispatchLoginSuccess = () => {
    authMe().then((u) => {
      const fromPath = loc.state?.from;
      const dest = u.mustChangePassword
        ? "/cambio-password"
        : (u.ruolo === "TRASPORTATORE" ? "/giro" : "/dashboard");
      // Se loc.state.from punta a una rotta non permessa al ruolo, ignoralo
      const allowFromPath =
        u.ruolo === "TRASPORTATORE"
          ? fromPath === "/giro"
          : (fromPath && fromPath !== "/giro" && fromPath !== "/cambio-password");
      window.location.replace(allowFromPath ? fromPath : dest);
    });
  };

  if (step === "loading") {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 size={28} className="animate-spin text-[#1B5E20]" /></div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1B5E20]/5 via-white to-[#F57C00]/5 p-4" data-testid="login-page">
      <Card className="w-full max-w-md p-6 sm:p-8 shadow-xl border-slate-200">
        <div className="flex flex-col items-center mb-6">
          <div className="h-16 w-16 rounded-2xl bg-[#1B5E20] flex items-center justify-center text-white mb-3 shadow-lg">
            <Droplets size={32} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Olio Esausto</h1>
          <p className="text-sm text-slate-500 mt-1">Gestione operativa ritiri</p>
        </div>

        {step === "choice" && (
          <div className="space-y-3" data-testid="login-choice">
            <div
              className="rounded-md bg-[#1B5E20]/5 border border-[#1B5E20]/20 px-3 py-2 text-xs text-slate-700"
              data-testid="login-roles-hint"
            >
              <strong>Admin, Ufficio e Trasportatori:</strong> accedi con email e password.<br />
              <strong>Trasportatori:</strong> puoi usare il PIN.
            </div>
            <Button
              onClick={() => setStep("password")}
              className="w-full h-16 bg-[#1B5E20] hover:bg-[#144818] text-white text-base font-semibold min-h-[60px]"
              data-testid="choice-password"
            >
              Accedi con email e password
            </Button>
            <Button
              variant="outline"
              onClick={() => setStep("pin-select")}
              className="w-full h-14 border-2 border-[#F57C00] text-[#F57C00] hover:bg-[#F57C00]/10 text-sm font-semibold min-h-[52px]"
              data-testid="choice-pin"
            >
              <KeyRound size={18} className="mr-2" /> Accedi con PIN (trasportatori)
            </Button>
          </div>
        )}

        {step === "password" && (
          <PasswordForm
            ctxLogin={ctxLogin}
            onSuccess={dispatchLoginSuccess}
            showPinLink={pinUsers.length > 0}
            onSwitchToPin={() => setStep("pin-select")}
          />
        )}

        {step === "pin-select" && (
          <PinUserSelect
            users={pinUsers}
            onSelect={(u) => { setPinSelectedUser(u); setStep("pin-keypad"); }}
            onBack={() => setStep("password")}
          />
        )}

        {step === "pin-keypad" && pinSelectedUser && (
          <PinKeypad
            user={pinSelectedUser}
            onSuccess={(d) => {
              localStorage.setItem(ACCESS_TOKEN_KEY, d.access_token);
              localStorage.setItem(REFRESH_TOKEN_KEY, d.refresh_token);
              dispatchLoginSuccess();
            }}
            onBack={() => setStep("pin-select")}
            onForgot={() => setStep("pin-recovery")}
          />
        )}

        {step === "pin-recovery" && pinSelectedUser && (
          <PinRecovery
            user={pinSelectedUser}
            onBack={() => setStep("pin-keypad")}
            onSuccess={() => { toast.success("PIN reimpostato. Effettua login."); setStep("pin-keypad"); }}
          />
        )}
      </Card>
    </div>
  );
}


function PasswordForm({ ctxLogin, onSuccess, showPinLink, onSwitchToPin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await ctxLogin(email, password);
      onSuccess();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="password-form">
      <div
        className="rounded-md bg-[#1B5E20]/5 border border-[#1B5E20]/20 px-3 py-2 text-xs text-slate-700"
        data-testid="login-roles-hint"
      >
        <strong>Admin, Ufficio e Trasportatori:</strong> accedi con email e password.{" "}
        {showPinLink && (
          <>
            <br />
            <strong>Trasportatori:</strong> puoi usare il{" "}
            <button
              type="button"
              onClick={onSwitchToPin}
              className="text-[#F57C00] font-semibold underline-offset-2 hover:underline"
              data-testid="hint-pin-link"
            >
              login con PIN
            </button>
            .
          </>
        )}
      </div>
      <div>
        <Label htmlFor="email" className="text-xs uppercase tracking-wider">Email</Label>
        <Input id="email" type="email" autoComplete="email" autoFocus value={email}
          onChange={(e) => setEmail(e.target.value)} className="h-12" required data-testid="email-input" />
      </div>
      <div>
        <Label htmlFor="password" className="text-xs uppercase tracking-wider">Password</Label>
        <div className="relative">
          <Input id="password" type={showPwd ? "text" : "password"} autoComplete="current-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="h-12 pr-10" required data-testid="password-input" />
          <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-slate-400" aria-label={showPwd ? "Nascondi" : "Mostra"}>
            {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
      {err && <div className="rounded-md bg-[#C62828]/10 border border-[#C62828]/30 px-3 py-2 text-sm text-[#C62828]" data-testid="login-error">{err}</div>}
      <Button type="submit" disabled={loading} className="w-full h-12 bg-[#1B5E20] hover:bg-[#144818] text-white font-semibold min-h-[44px]" data-testid="login-submit">
        {loading ? <Loader2 size={18} className="animate-spin" /> : "Accedi"}
      </Button>
      {showPinLink && (
        <button
          type="button"
          onClick={onSwitchToPin}
          className="w-full inline-flex items-center justify-center gap-2 text-xs text-[#F57C00] font-semibold hover:underline pt-1 min-h-[40px]"
          data-testid="switch-to-pin"
        >
          <KeyRound size={14} />
          Sei un trasportatore? Accedi con PIN
        </button>
      )}
    </form>
  );
}


function PinUserSelect({ users, onSelect, onBack }) {
  return (
    <div className="space-y-3" data-testid="pin-user-select">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1">
        <ArrowLeft size={14} /> Indietro
      </button>
      <h2 className="text-lg font-semibold text-slate-900">Chi sei?</h2>
      <div className="grid gap-2">
        {users.map((u) => (
          <Button
            key={u.id}
            onClick={() => onSelect(u)}
            variant="outline"
            className="h-14 justify-start text-base"
            data-testid={`pin-user-${u.id}`}
          >
            <div className="text-left">
              <div className="font-semibold text-slate-900">{u.nome} {u.cognome}</div>
              <div className="text-xs text-slate-500">{u.email}</div>
            </div>
          </Button>
        ))}
      </div>
    </div>
  );
}


function PinKeypad({ user, onSuccess, onBack, onForgot }) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Cifre 1-9 (no 0 per evitare PIN deboli, requirement utente)
  const digits = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

  useEffect(() => {
    if (pin.length === 4) {
      setLoading(true);
      pinLogin(user.email, pin)
        .then((d) => onSuccess(d))
        .catch((ex) => {
          setErr(ex.response?.data?.detail || "PIN errato");
          setPin("");
        })
        .finally(() => setLoading(false));
    }
  }, [pin]); // eslint-disable-line

  const tap = (d) => { if (pin.length < 4 && !loading) { setPin(pin + d); setErr(""); } };
  const backspace = () => { if (!loading) setPin(pin.slice(0, -1)); };

  return (
    <div className="space-y-4" data-testid="pin-keypad">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1">
        <ArrowLeft size={14} /> Annulla
      </button>
      <div className="text-center">
        <div className="text-sm text-slate-500">Bentornato</div>
        <div className="text-lg font-semibold text-slate-900">{user.nome} {user.cognome}</div>
        <div className="text-xs text-slate-400">Inserisci il tuo PIN a 4 cifre</div>
      </div>
      {/* Pallini PIN */}
      <div className="flex justify-center gap-3 my-4" data-testid="pin-dots">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`h-4 w-4 rounded-full border-2 ${pin.length > i ? "bg-[#1B5E20] border-[#1B5E20]" : "border-slate-300"}`} />
        ))}
      </div>
      {err && <div className="text-center text-sm text-[#C62828]" data-testid="pin-error">{err}</div>}
      {loading && <div className="text-center"><Loader2 size={20} className="inline animate-spin text-[#1B5E20]" /></div>}
      {/* Tastierino */}
      <div className="grid grid-cols-3 gap-2 max-w-[280px] mx-auto">
        {digits.map((d) => (
          <button
            key={d}
            onClick={() => tap(d)}
            disabled={loading}
            className="h-16 w-16 sm:h-[68px] sm:w-[68px] rounded-2xl bg-white border-2 border-slate-200 hover:bg-slate-50 active:bg-slate-100 active:scale-95 transition-all text-2xl font-bold text-slate-900 disabled:opacity-50"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid={`pin-digit-${d}`}
          >
            {d}
          </button>
        ))}
        <div />
        <button
          onClick={backspace}
          disabled={loading || pin.length === 0}
          className="h-16 rounded-2xl bg-slate-100 hover:bg-slate-200 active:scale-95 text-sm font-semibold text-slate-700 disabled:opacity-50"
          data-testid="pin-backspace"
        >
          ← Cancella
        </button>
      </div>
      <button onClick={onForgot} className="w-full text-xs text-[#1565C0] hover:underline pt-2 text-center" data-testid="pin-forgot">
        PIN dimenticato?
      </button>
    </div>
  );
}


function PinRecovery({ user, onBack, onSuccess }) {
  const [domanda, setDomanda] = useState("");
  const [risposta, setRisposta] = useState("");
  const [newPin, setNewPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    pinRecoveryQuestion(user.email).then((d) => setDomanda(d.domanda)).catch(() => setDomanda("Recupero non disponibile"));
  }, [user]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (!/^[1-9]{4}$/.test(newPin)) throw new Error("Il PIN deve essere 4 cifre da 1 a 9");
      await pinResetWithRecovery(user.email, risposta, newPin);
      onSuccess();
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="pin-recovery">
      <button type="button" onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1">
        <ArrowLeft size={14} /> Torna al PIN
      </button>
      <div className="text-sm font-semibold text-slate-900">Recupero PIN per {user.email}</div>
      <div>
        <Label className="text-xs uppercase tracking-wider">{domanda || "Caricamento..."}</Label>
        <Input value={risposta} onChange={(e) => setRisposta(e.target.value)} required className="h-12" data-testid="pin-recovery-answer" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider">Nuovo PIN (4 cifre 1-9)</Label>
        <Input type="text" inputMode="numeric" pattern="[1-9]{4}" maxLength={4} value={newPin}
          onChange={(e) => setNewPin(e.target.value)} required className="h-12 tracking-widest text-center text-lg" data-testid="pin-recovery-new" />
      </div>
      {err && <div className="text-sm text-[#C62828]" data-testid="pin-recovery-error">{err}</div>}
      <Button type="submit" disabled={loading} className="w-full h-12 bg-[#F57C00] hover:bg-[#E65100] text-white" data-testid="pin-recovery-submit">
        {loading ? <Loader2 size={18} className="animate-spin" /> : "Reimposta PIN"}
      </Button>
    </form>
  );
}
